import { apiClient } from '@/lib/api/client'

export interface AnalyticsOverview {
  summary: {
    total_users: number
    total_pages: number
    total_media: number
    total_categories: number
    total_tags: number
  }
  user_metrics: {
    total: number
    active: number
    new_today: number
    new_this_week: number
    new_this_month: number
    new_last_30_days: number
  }
  content_metrics: {
    total_pages: number
    published_pages: number
    draft_pages: number
    pages_this_month: number
  }
  activity_metrics: {
    total: number
    today: number
    this_week: number
    by_module: Record<string, number>
    by_action: Record<string, number>
  }
  trends: {
    user_registration: {
      labels: string[]
      data: number[]
    }
    page_creation: {
      labels: string[]
      data: number[]
    }
  }
}

/** Tickets overview filters */
export interface TicketsOverviewParams {
  service_type_id?: number | null
  date_from?: string
  date_to?: string
  statistics_type?: 'tickets' | 'agents'
}

/** Ticket detail row (statistics_type = tickets): ticket request + status + SLA clock */
export interface TicketsOverviewRowDetail {
  id: number
  request_number: string
  user_id: number | null
  service_type_id: number | null
  service_type_name: string | null
  description: string | null
  contact_name: string | null
  contact_email: string | null
  contact_number: string | null
  ticket_status_id: number | null
  status_code: string | null
  status_label: string | null
  assigned_to: number | null
  submitted_at: string | null
  resolved_at: string | null
  closed_at: string | null
  created_at: string | null
  updated_at: string | null
  sla_clock_id: number | null
  sla_clock_started_at: string | null
  sla_clock_due_at: string | null
  sla_clock_response_due_at: string | null
  sla_clock_status: string | null
  sla_clock_breached_at: string | null
  sla_clock_completed_at: string | null
}

/** Agent row (statistics_type = agents) */
export interface TicketsOverviewRowAgent {
  agent_id: number
  agent_name: string
  open_count: number
  resolved_count: number
  total_count: number
}

export type TicketsOverviewTableRow = TicketsOverviewRowDetail | TicketsOverviewRowAgent

export interface TicketsOverviewResponse {
  chart: { labels: string[]; data: number[] }
  table: TicketsOverviewTableRow[]
  statistics_type: 'tickets' | 'agents'
}

export const analyticsService = {
  async getOverview(): Promise<AnalyticsOverview> {
    const response = await apiClient.get('/analytics/overview')
    return response.data.data
  },

  async getTicketsOverview(params: TicketsOverviewParams = {}): Promise<TicketsOverviewResponse> {
    const searchParams = new URLSearchParams()
    if (params.service_type_id != null) {
      searchParams.set('service_type_id', String(params.service_type_id))
    }
    if (params.date_from) searchParams.set('date_from', params.date_from)
    if (params.date_to) searchParams.set('date_to', params.date_to)
    if (params.statistics_type) searchParams.set('statistics_type', params.statistics_type)
    const response = await apiClient.get(
      `/analytics/tickets-overview?${searchParams.toString()}`
    )
    return response.data.data
  },
}

